import { BasicAttrTextEnum } from '@/enums'
import type { EquipSpecialAttrType } from '@/types/equip'
import type { EquipAffixType } from '@/types/equip-affix'
import type { BaseUserAttrType } from '@/types/user'
import { formatNumber } from '@/utils'
import { elementRestraint, relicElementNameMap, relicIcon } from '@/views/item/data'

export const monsterAffixCollection: EquipAffixType = {
  counterAtk: {
    name: '反击',
    desc: ({ multiplier }) => {
      return `受到多段攻击时，免疫 <span class="grow">${multiplier}%</span> 的伤害`
    },
  },
  resist: {
    name: '抵抗',
    desc: ({ hpPercent, value }) => {
      return `血量低于 <span class="info">${hpPercent}%</span>时，增加 <span class="grow">${value}</span> 防御`
    },
  },
  survival: {
    name: '求生',
    desc: ({ hpPercent, value }) => {
      return `血量低于 <span class="info">${hpPercent}%</span>时，增加 <span class="grow">${value}%</span> 闪避率`
    },
  },
  weak: {
    name: '虚弱',
    desc: ({ value, multiplier }) => {
      if (value) {
        return `降低玩家 <span class="shrink">${multiplier}</span> 攻击`
      } else {
        return `降低玩家 <span class="shrink">${multiplier}%</span> 攻击`
      }
    },
  },
  fearless: {
    name: '无畏',
    desc: ({ hpPercent, value }) => {
      return `血量高于 <span class="info">${hpPercent}%</span>时，增加 <span class="grow">${value}</span> 防御`
    },
  },
  bedrock: {
    name: '磐石',
    desc: ({ rate, value }) => {
      return `受到攻击时，<span class="info">${rate}%</span> 概率增加 <span class="grow">${value}</span> 防御`
    },
  },
  slow: {
    name: '迟缓',
    desc: ({ value }) => {
      return `降低玩家 <span class="shrink">${value}%</span> 攻击速度`
    },
  },
  stout: {
    name: '坚韧',
    desc: ({ hpPercent, value }) => {
      return `血量低于 <span class="info">${hpPercent}%</span>时，增加 <span class="grow">${value}</span> 防御`
    },
  },
  vampirism: {
    name: '吸血',
    desc: ({ value }) => {
      return `攻击 <span class="info">未命中</span>时，怪物回复 <span class="grow">${Math.round(value * 100)}%</span> 血量`
    },
  },
  intimidate: {
    name: '恐吓',
    desc: ({ value, multiplier }) => {
      if (value) {
        return `降低玩家 <span class="shrink">${multiplier}</span> 破防`
      } else {
        return `降低玩家 <span class="shrink">${multiplier}%</span> 破防`
      }
    },
  },
  repress: {
    name: '镇压',
    desc: ({ multiplier }) => {
      return `受到暴击时，免疫 <span class="grow">${multiplier}%</span> 的伤害`
    },
  },
  restore: {
    name: '修复',
    desc: ({ hpPercent, value }) => {
      return `血量降低至 <span class="info">${hpPercent}%</span> 以下时，免疫一次伤害并恢复 <span class="grow">${value}%</span> 血量（仅触发一次）`
    },
  },
  palsy: {
    name: '麻痹',
    desc: ({ multiplier }) => {
      return `受到非暴击攻击时，免疫 <span class="grow">${multiplier}%</span> 的伤害`
    },
  },
  devour: {
    name: '吞噬',
    desc: ({ triggerValue, multiplier }) => {
      return `伤害结算时，若防御高于 <span class="info">${triggerValue}</span>，免疫 <span class="grow">${multiplier}%</span> 的伤害`
    },
  },
  etanglement: {
    name: '缠绕',
    desc: ({ multiplier }) => {
      return `降低玩家攻击速度的 <span class="shrink">${multiplier}%</span>`
    },
  },
  curse: {
    name: '诅咒',
    desc: ({ multiplier }) => {
      return `降低玩家命中率的 <span class="shrink">${multiplier}%</span>`
    },
  },
  burn: {
    name: '灼烧',
    desc: ({ triggerValue, multiplier }) => {
      return `受到特效大于 <span class="info">${triggerValue}</span> 个时，免疫 <span class="grow">${multiplier}%</span> 的伤害`
    },
  },
  willpower: {
    name: '意志',
    desc: ({ hpPercent, value }) => {
      return `血量低于 <span class="info">${hpPercent}% </span>时，增加 <span class="grow">${value}%</span> 抗暴率`
    },
  },
  magicCrystal: {
    name: '魔法水晶',
    desc: ({ time = 60, hpPercent = 65 }) => {
      return `水晶龙后背潜藏着巨大能量的魔法水晶，若不及时打碎，则水晶将激发出巨大的能力（<span class="info">${time}</span>秒后，若血量高于 <span class="info">${hpPercent}%</span>，激发 <span class="grow">水晶能量</span>）`
    },
  },
  crystalEnergy: {
    name: '水晶能量',
    desc: ({ value = 500 }) => {
      return `<span class="info">激发时</span>，增加 <span class="grow">${value}</span> 防御`
    },
  },
  thunderWitchcraft: {
    name: '雷霆巫术',
    desc: () => {
      return '血量低于<span class="info">50%</span> 时，发动 <span class="grow">雷霆护盾</span>，同时发动 <span class="grow">麻痹</span>（受到非暴击攻击时，免疫 <span class="grow">80%</span> 的伤害）'
    },
  },
  thunderShield: {
    name: '雷霆护盾',
    desc: () => {
      return '<span class="info">发动后</span>，增加 <span class="grow">50%</span> 抗暴率'
    },
  },
  frostWitchcraft: {
    name: '冰霜巫术',
    desc: () => {
      return '血量低于<span class="info">50%</span> 时，发动 <span class="grow">冰霜护盾</span>，同时发动 <span class="grow">吸血</span>（攻击 <span class="info">未命中</span>时，怪物回复 <span class="grow">2%</span> 血量)'
    },
  },
  frostShield: {
    name: '冰霜护盾',
    desc: () => {
      return '<span class="info">发动后</span>，增加 <span class="grow">55%</span> 闪避率'
    },
  },
  fireWitchcraft: {
    name: '烈焰巫术',
    desc: () => {
      return '血量低于<span class="info">50%</span> 时，发动 <span class="grow">烈焰护盾</span>，同时发动 <span class="grow">吞噬</span>（伤害结算时，若防御高于 <span class="info">200</span>，免疫 <span class="grow">80%</span> 的伤害)'
    },
  },
  fireShield: {
    name: '烈焰护盾',
    desc: () => {
      return '<span class="info">发动后</span>，增加 <span class="grow">250</span> 防御'
    },
  },
  senile: {
    name: '衰老',
    desc: ({ time, maxValue }) => {
      return `<span class="info">${time / 60}分钟内</span>，持续降低玩家攻击力，最高降低 <span class="shrink">${maxValue * 100}%</span>，直至战斗结束`
    },
  },
  terror: {
    name: '惊骇',
    desc: ({ hpPercent, multiplier }) => {
      return `血量低于 <span class="info">${hpPercent}%</span> 时，降低玩家命中率的 <span class="shrink">${multiplier}%</span>`
    },
  },
  nirvana: {
    name: '涅槃',
    desc: ({ hpPercent, value, times }) => {
      return `血量降低至 <span class="info">${hpPercent}%</span> 以下时，免疫一次伤害并恢复 <span class="grow">${value}%</span> 血量（可触发 <span class="grow">${times}</span>次）`
    },
  },
  broiling: {
    name: '炙热',
    desc: ({ triggerValue, multiplier }) => {
      return `第 <span class="info">3</span> 次<span class="grow">涅槃</span>后，受到特效大于 <span class="info">${triggerValue}</span> 个时，免疫 <span class="grow">${multiplier}%</span> 的伤害`
    },
  },
  sacredArmor: {
    name: '神圣铠甲',
    desc: ({ hpPercent }) => {
      return `血量低于 <span class="info">${hpPercent}%</span> 以下时，免疫<span class="real-damage">真实伤害</span>以外所有伤害`
    },
  },
  liveOn: {
    name: '生息',
    desc: ({ hpPercent, value }) => {
      return `血量高于 <span class="info">${hpPercent}%</span> 时，每次受击回复 <span class="grow">${formatNumber(value * 100, 1)}%</span> 血量`
    },
  },
  deathStare: {
    name: '死亡凝视',
    desc: ({ triggerValue, rate }) => {
      return `每 <span class="info">2秒</span>受击次数超过 <span class="info">${
        triggerValue * 2
      }次</span>时，有 <span class="info">${rate}%</span> 概率直接<span class="shrink">失败</span>`
    },
  },
  deathStare2: {
    name: '死亡凝视',
    desc: ({ triggerValue, rate }) => {
      return `每 <span class="info">2秒</span>受击次数超过 <span class="info">${
        triggerValue * 2
      }次</span>时，有 <span class="info">${rate}%</span> 概率免疫伤害`
    },
  },
  thorn: {
    name: '荆棘',
    desc: ({ triggerValue, multiplier }) => {
      return `单次受到伤害大于血量的 <span class="info">${triggerValue}%</span> 时，免疫 <span class="grow">${multiplier}%</span> 的伤害`
    },
  },
  antibody: {
    name: '抗体',
    desc: ({ triggerValue, multiplier }) => {
      return `受到特效小于 <span class="info">${triggerValue}</span> 个时，免疫 <span class="grow">${multiplier}%</span> 的伤害`
    },
  },
  incorporeality: {
    name: '虚体',
    desc: ({ multiplier }) => {
      return `免疫 非<span class="realdamage">真实伤害</span>的 <span class="grow">${multiplier}%</span>`
    },
  },
  strong: {
    name: '坚固',
    desc: () => {
      return `获得等同于玩家攻击的<span class="info">防御</span>`
    },
  },
  stun: {
    name: '击晕',
    desc: ({ percent }) => {
      return `${percent}% 概率，免疫 <span class="info">伤害型特效</span>`
    },
  },
  thirstyBlood: {
    name: '渴血',
    desc: ({ value }) => {
      return `攻击 <span class="info">未命中</span> 或 <span class="info">未暴击</span>时，怪物回复 <span class="grow">${Math.round(
        value * 100,
      )}%</span> 血量`
    },
  },
  recovery: {
    name: '复苏',
    desc: ({ value, times }) => {
      return `受到 <span class="info">致命伤害</span>时，免疫一次伤害并恢复 <span class="grow">${value}%</span> 血量（可触发 <span class="grow">${times}</span>次）`
    },
  },
  disdain: {
    name: '蔑视',
    desc: () => {
      return `<span class="info">防御</span> 无法被降低`
    },
  },
  elementDef: {
    name: '',
    explainName: ({ element }) => `${relicElementNameMap[element]}元素护盾`,
    desc: ({ element }) => {
      const restraintElement = elementRestraint[element]
      const icon = relicIcon[restraintElement]
      return `受到 <span class="info">${icon}${relicElementNameMap[restraintElement]}元素</span>伤害时，增加玩家 <span class="grow">20%</span>的伤害；受到 <span class="info">${relicIcon[element]}${relicElementNameMap[element]}元素</span>伤害时，伤害不变；受到其他元素伤害时降低 <span class="shrink">20%</span>的伤害`
    },
  },
  superHard: {
    name: '巨硬',
    desc: () => {
      return '强制将受到的攻击降低为 <span class="shrink">1</span>'
    },
  },
  crystalSpike: {
    name: '水晶尖刺',
    desc: ({ hpPercent }) => {
      return `血量低于 <span class="info">${hpPercent}%</span> 时，免疫多段攻击`
    },
  },
  crystalBarrier: {
    name: '水晶屏障',
    desc: ({ minHpPercent, maxHpPercent, multiplier }) => {
      return `血量介于 <span class="info">${minHpPercent}%</span> ~ <span class="info">${maxHpPercent}%</span> 时，免疫 非<span class="real-damage">真实伤害</span>的 <span class="grow">${multiplier}%</span>`
    },
  },
  poisonousVenom: {
    name: '毒蛊',
    desc: ({ time, maxValue }) => {
      return `<span class="info">${time / 60}分钟内</span>，持续降低玩家破防，最高降低 <span class="shrink">${maxValue * 100}%</span>，直至战斗结束`
    },
  },
  poisonousFang: {
    name: '毒牙',
    desc: ({ hpPercent, value }) => {
      return `血量高于 <span class="info">${hpPercent}%</span> 时，降低玩家 <span class="shrink">${value}%</span>暴击率`
    },
  },
  poisonousFog: {
    name: '毒雾',
    desc: ({ hpPercent, crtdef, evs, def }) => {
      return `血量低于 <span class="info">${hpPercent}%</span> 时，降低自身抗暴性 <span class="shrink">${crtdef}%</span>，增加自身闪避率 <span class="grow">${evs}%</span>，增加 <span class="grow">${def}</span> 防御`
    },
  },
  thunderstrike: {
    name: '雷击',
    desc: ({ minHpPercent, maxHpPercent, multiplier }) => {
      return `血量介于 <span class="info">${minHpPercent}%</span> ~ <span class="info">${maxHpPercent}%</span> 时，免疫 非暴击攻击的 <span class="grow">${multiplier}%</span>`
    },
  },
  flame: {
    name: '爆炎',
    desc: ({ minHpPercent, maxHpPercent, multiplier, triggerValue }) => {
      return `血量介于 <span class="info">${minHpPercent}%</span> ~ <span class="info">${maxHpPercent}%</span> 时，受到特效大于 <span class="info">${triggerValue}</span> 个时，免疫 <span class="grow">${multiplier}%</span> 的伤害`
    },
  },
  iceSeal: {
    name: '冰封',
    desc: ({ minHpPercent, maxHpPercent, value }) => {
      return `血量介于 <span class="info">${minHpPercent}%</span> ~ <span class="info">${maxHpPercent}%</span> 时，降低玩家 <span class="shrink">${value}</span>攻击`
    },
  },
  invisible: {
    name: '隐形',
    desc: ({ hpPercent }) => {
      return `血量低于 <span class="info">${hpPercent}%</span> 时，<span class="shrink">无法</span>被命中`
    },
  },
  magicImmune: {
    name: '魔免',
    desc: ({ multiplier }) => {
      return `受到技能伤害时，免疫 <span class="grow">${multiplier}%</span> 的伤害`
    },
  },
  windImmune: {
    name: '风免',
    desc: ({ multiplier }) => {
      return `受到风技能伤害时，免疫 <span class="grow">${multiplier}%</span> 的伤害`
    },
  },
  fireImmune: {
    name: '火免',
    desc: ({ multiplier }) => {
      return `受到火技能伤害时，免疫 <span class="grow">${multiplier}%</span> 的伤害`
    },
  },
  waterImmune: {
    name: '水免',
    desc: ({ multiplier }) => {
      return `受到水技能伤害时，免疫 <span class="grow">${multiplier}%</span> 的伤害`
    },
  },
  soilImmune: {
    name: '土免',
    desc: ({ multiplier }) => {
      return `受到土技能伤害时，免疫 <span class="grow">${multiplier}%</span> 的伤害`
    },
  },
}

export function explainMonsterAffix(affixList: EquipSpecialAttrType[]) {
  return affixList.map((item) => {
    const equipAffix = monsterAffixCollection[item.key]
    const name = equipAffix.explainName?.(item.data) || equipAffix.name
    const desc = equipAffix.desc(item.data)
    return `<span class="special">${name}</span>：${desc}`
  })
}

export function explainMonsterAttrsWeight(attrsWeight: Partial<BaseUserAttrType> = {}) {
  return findMaxKeys(attrsWeight)
    .map((item) => BasicAttrTextEnum[item])
    .join('、')
}
function findMaxKeys(obj: Partial<BaseUserAttrType>) {
  let maxVal = -Infinity
  let maxKeys: (keyof BaseUserAttrType)[] = []

  let key: keyof BaseUserAttrType
  for (key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key]
      if (!value) continue
      if (value > maxVal) {
        maxVal = value
        maxKeys = [key] // Start new array with current key
      } else if (value === maxVal) {
        maxKeys.push(key) // Add to existing max keys
      }
    }
  }

  return maxKeys
}
